import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

/**
 * Hook to track keyboard visibility on mobile devices.
 * Returns true when the keyboard is visible, false otherwise.
 *
 * Uses the visualViewport API to detect keyboard appearance by monitoring
 * viewport height changes. When the keyboard opens, the visual viewport shrinks.
 */
export function useKeyboardVisible() {
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  useEffect(() => {
    // Only set up listeners on native platforms
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    // Use visualViewport API to detect keyboard
    const viewport = window.visualViewport;
    if (!viewport) {
      return;
    }

    // Store the initial height to compare against
    let initialHeight = viewport.height;

    const handleResize = () => {
      // If viewport height shrinks significantly (more than 150px), keyboard is likely open
      // We use 150px as a threshold to avoid false positives from other UI changes
      const heightDiff = initialHeight - viewport.height;
      const keyboardOpen = heightDiff > 150;

      setIsKeyboardVisible(keyboardOpen);
    };

    // Also track scroll to update initial height when scrolling without keyboard
    const handleScroll = () => {
      // Only update initial height when keyboard is not visible
      if (!isKeyboardVisible && viewport.height > initialHeight * 0.8) {
        initialHeight = viewport.height;
      }
    };

    viewport.addEventListener('resize', handleResize);
    viewport.addEventListener('scroll', handleScroll);

    // Cleanup listeners on unmount
    return () => {
      viewport.removeEventListener('resize', handleResize);
      viewport.removeEventListener('scroll', handleScroll);
    };
  }, [isKeyboardVisible]);

  return isKeyboardVisible;
}

/**
 * Hook that returns the visual viewport height in pixels.
 * On iOS Capacitor with Keyboard resize: "none", the webview stays full-size
 * and the keyboard overlays on top. This hook tracks the visible area height
 * so containers (like chat sheets) can size themselves above the keyboard.
 *
 * Returns null on non-native platforms (use CSS 100dvh instead).
 */
export function useVisualViewportHeight() {
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const viewport = window.visualViewport;
    if (!viewport) {
      return;
    }

    const handleResize = () => {
      setViewportHeight(viewport.height);
    };

    // Set initial value
    handleResize();

    viewport.addEventListener('resize', handleResize);

    return () => {
      viewport.removeEventListener('resize', handleResize);
    };
  }, []);

  return viewportHeight;
}

/**
 * Hook that returns the keyboard height (distance from bottom of visual viewport
 * to bottom of layout viewport). Useful for positioning fixed-bottom elements
 * above the keyboard when using Keyboard resize: "none".
 *
 * Returns 0 when keyboard is hidden or on non-native platforms.
 */
export function useKeyboardOffset() {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const viewport = window.visualViewport;
    if (!viewport) {
      return;
    }

    const handleResize = () => {
      // The keyboard offset is the difference between the full window height
      // and the visual viewport (visible area above keyboard)
      const keyboardHeight = window.innerHeight - viewport.height - viewport.offsetTop;
      setOffset(Math.max(0, keyboardHeight));
    };

    handleResize();

    viewport.addEventListener('resize', handleResize);

    return () => {
      viewport.removeEventListener('resize', handleResize);
    };
  }, []);

  return offset;
}
